import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import axiosWrapper from '../utils/AxiosWrapper'
import { showBrowserNotification } from '../utils/browserNotify'

function roleMatchesNotice(role, noticeType) {
  const r = String(role || '').toLowerCase()
  const t = String(noticeType || '')
  if (r === 'student') return t === 'student' || t === 'both'
  if (r === 'faculty') return t === 'faculty' || t === 'both'
  return false
}

/**
 * Background poll for /notice — backend has no realtime channel yet.
 * Shows a desktop notification once per new notice id (when permission granted & tab hidden).
 */
export default function NoticeNotificationPoller({ intervalMs = 90_000 }) {
  const navigate = useNavigate()
  const seenIdsRef = useRef(new Set())
  const primedRef = useRef(false)

  useEffect(() => {
    const token = localStorage.getItem('userToken')
    const role = localStorage.getItem('userType')
    if (!token || !['student', 'faculty'].includes(String(role || '').toLowerCase())) {
      return undefined
    }

    let cancelled = false

    const basePath =
      String(role || '').toLowerCase() === 'faculty'
        ? '/faculty'
        : String(role || '').toLowerCase() === 'student'
          ? '/student'
          : null
    const noticeHref = basePath ? `${basePath}?page=notice` : '/'

    const tick = async () => {
      if (cancelled) return
      try {
        let rows = []
        try {
          const res = await axiosWrapper.get('/notice', {
            headers: { Authorization: `Bearer ${token}` },
          })
          if (
            res.data?.success &&
            Array.isArray(res.data.data) &&
            res.data.data.length
          ) {
            rows = res.data.data
          }
        } catch (e) {
          if (e?.response?.status === 404) rows = []
          else return
        }
        if (cancelled) return

        const list = [...rows].filter((n) => roleMatchesNotice(role, n?.type))
        list.sort(
          (a, b) =>
            new Date(b.createdAt || 0).getTime() -
            new Date(a.createdAt || 0).getTime()
        )

        const newItems = []
        for (const n of list.slice(0, 25)) {
          const id = String(n._id || '')
          if (!id || seenIdsRef.current.has(id)) continue
          seenIdsRef.current.add(id)
          if (primedRef.current) newItems.push(n)
        }

        if (!primedRef.current) {
          primedRef.current = true
          return
        }

        for (const n of newItems) {
          const desc = String(n.description || '').trim()
          const body =
            desc.length > 160 ? `${desc.slice(0, 157)}…` : desc || 'Tap to open notices.'
          showBrowserNotification(`New notice: ${n.title || 'Announcement'}`, body, {
            tag: `notice-${n._id}`,
            navigateFn: () => navigate(noticeHref),
          })
        }
      } catch {
        /* noop */
      }
    }

    tick()
    const id = window.setInterval(tick, intervalMs)

    const onResume = () => {
      tick()
    }
    document.addEventListener('visibilitychange', onResume)

    return () => {
      cancelled = true
      window.clearInterval(id)
      document.removeEventListener('visibilitychange', onResume)
    }
  }, [intervalMs, navigate])

  return null
}
